// Procedure tab — sub-tabs Airport / Departure / Arrival / Approach.
//
// Wired to FAA d-TPP via /api/plates/airport. The plate viewer is the
// shared <PlateViewer /> used by /plates so the experience is the same
// from both entry points (airport detail panel, dedicated plates page).
//
// Each plate row shows an inline georef-confidence badge so pilots know
// before tapping "View on Map" whether the overlay will be accurate.
// The Arrival subtab (STAR plates) supports view-on-map just like
// Approach, backed by the STAR sibling georef blob shipped 2026-05-16.

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  fetchAirportPlates,
  fetchAirportGeorefStatus,
  groupPlates,
  chartCodeLabel,
  plateGeorefQuality,
  deriveProcIdent,
  type PlateRecord,
  type PlatesResponse,
  type PlateGeorefIndexRow,
  type PlateGeorefQuality,
} from '../../lib/plates';
import { CirclingMinimaChip } from '../CirclingMinimaChip';
import { Icon } from '../../shell/Icon';
import { GeorefBadge } from '../../components/GeorefBadge';
import { PlateViewer } from '../../plates/PlateViewer';
import { usePublicationsStore, statusTextClass, statusLabel } from '../../stores/publicationsStore';
import { useUiStore } from '../../stores/uiStore';
import { useNavigate } from 'react-router-dom';
import { PlateCorrectionEditor, type EditorFix } from '../../plates/PlateCorrectionEditor';
import { fetchNasrAirport } from '../../lib/airportNasr';
import {
  fetchSuggestedWaypoints,
  suggestedToEditorFix,
  type SuggestedWaypointsResponse,
} from '../../lib/plateSuggestedWaypoints';
import { useProceduresStore } from '../../stores/proceduresStore';

type ProcKey = 'airport' | 'departure' | 'arrival' | 'approach';

const SUBTABS: Array<{ key: ProcKey; label: string }> = [
  { key: 'airport', label: 'Airport' },
  { key: 'departure', label: 'Departure' },
  { key: 'arrival', label: 'Arrival' },
  { key: 'approach', label: 'Approach' },
];

export function ProcedureTab({
  ident,
  openPdfName,
  autoEditor = false,
  airportElevFt = 0,
}: {
  ident: string;
  /** PDF name to auto-select on first data load (deep-link from queue). */
  openPdfName?: string;
  /** If true, auto-launch PlateCorrectionEditor after selecting the plate. */
  autoEditor?: boolean;
  /** Airport field elevation (MSL ft) from the airport record. Used for
   *  circling minima AGL display and expanded-criteria radius lookup. */
  airportElevFt?: number;
}) {
  const [sub, setSub] = useState<ProcKey>('approach');
  const [data, setData] = useState<PlatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PlateRecord | null>(null);
  const [georefMap, setGeorefMap] = useState<Map<string, PlateGeorefIndexRow>>(new Map());
  // State for the correction editor, separate from PlateViewer so both
  // can be open simultaneously (view plate + edit anchors).
  const [editorPlate, setEditorPlate] = useState<PlateRecord | null>(null);
  const nav = useNavigate();
  const setPlateOverlay = useUiStore((s) => s.setPlateOverlay);
  const loadAirport = useProceduresStore((s) => s.loadAirport);
  const [editorFixes, setEditorFixes] = useState<EditorFix[]>([]);
  const [editorCourse, setEditorCourse] = useState<number | undefined>();
  const [editorSuggesterMode, setEditorSuggesterMode] = useState<SuggestedWaypointsResponse['mode'] | null>(null);
  // Track whether we've already applied the one-shot deep-link so we
  // don't re-apply on subsequent renders (e.g. after a data refresh).
  const deepLinkApplied = useRef(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    setGeorefMap(new Map());
    const ac = new AbortController();
    fetchAirportPlates(ident, ac.signal)
      .then((d) => {
        setData(d);
        setLoading(false);
        // Best-effort fetch of per-plate georef status. Single round
        // trip; harmless if the endpoint is unavailable (we just render
        // 'unknown' badges).
        if (d.cycle) {
          fetchAirportGeorefStatus(ident, d.cycle, ac.signal)
            .then(setGeorefMap)
            .catch(() => {
              // network or perms error; leave map empty
            });
        }
      })
      .catch((e) => { setError(e?.message || 'fetch failed'); setLoading(false); });
    return () => ac.abort();
  }, [ident]);

  // Refetch georef status when the tab regains focus, so that approving a
  // plate correction on /admin/plate-corrections/review and navigating
  // back here immediately shows the new ‘Fixed’ badge instead of the
  // stale failed/LC indicator. Boss report 2026-05-25: 'after correcting
  // the plate and approving it, the status still does not appear to
  // update in the airports procedures tab'. The cycle is read from the
  // already-loaded plates payload; we don't refetch the full plate list,
  // only the cheap georef-status index.
  useEffect(() => {
    function onFocusOrVisible() {
      if (document.visibilityState !== 'visible') return;
      const cyc = data?.cycle;
      if (!cyc) return;
      fetchAirportGeorefStatus(ident, cyc)
        .then(setGeorefMap)
        .catch(() => undefined);
    }
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    return () => {
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
    };
  }, [ident, data?.cycle]);

  const groups = useMemo(() => (data ? groupPlates(data.plates) : null), [data]);

  // Default the sub-tab to whichever bucket has plates first. Most
  // pilots opening the panel want the approach list; only fall through
  // if approaches are empty.
  useEffect(() => {
    if (!groups) return;
    if (sub === 'approach' && groups.approach.length === 0) {
      if (groups.arrival.length > 0) setSub('arrival');
      else if (groups.diagram.length > 0) setSub('airport');
      else if (groups.departure.length > 0) setSub('departure');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Reset the one-shot guard whenever a new deep-link arrives so
  // re-navigating to the same airport fires the editor again.
  useEffect(() => {
    if (!openPdfName) return;
    deepLinkApplied.current = false;
  }, [openPdfName]);

  // Deep-link: auto-select the target plate and optionally open the
  // correction editor. Runs when data is loaded OR when openPdfName changes
  // (covers the already-mounted case where AirportDetail didn't remount).
  useEffect(() => {
    if (!groups || !data || !openPdfName || deepLinkApplied.current) return;
    deepLinkApplied.current = true;

    // Search every bucket for the matching plate.
    const allPlates = [
      ...groups.approach,
      ...groups.arrival,
      ...groups.departure,
      ...groups.diagram,
      ...groups.minimums,
      ...groups.other,
    ];
    const plate = allPlates.find(
      (p) => p.pdfName.toUpperCase() === openPdfName.toUpperCase(),
    );
    if (!plate) return;

    // Switch to the sub-tab that owns this plate.
    if (groups.approach.some((p) => p.pdfName === plate.pdfName)) setSub('approach');
    else if (groups.arrival.some((p) => p.pdfName === plate.pdfName)) setSub('arrival');
    else if (groups.departure.some((p) => p.pdfName === plate.pdfName)) setSub('departure');
    else setSub('airport');

    // Open the PlateViewer — BUT only if we're not also auto-launching
    // the correction editor. The editor is its own full-screen modal
    // that renders the same plate, and stacking the two created visual
    // glitches (boss report 2026-05-19 21 PT: editor's left pane
    // showed the PlateViewer behind the right aside on some viewports
    // because the two modals live in different stacking contexts).
    if (!autoEditor) {
      setSelected(plate);
      return;
    }

    // Auto-launch the correction editor: load CIFP fixes first, then
    // augment with NASR-suggested waypoints (always — the suggester is
    // additive and ranks CIFP first via the source-tabbed checklist).
    setEditorPlate(plate);
    setEditorSuggesterMode(null);
    loadAirport(ident)
      .then(async (procs) => {
        const pdfUpper = plate.pdfName.toUpperCase();
        const procIdent = plate.chartName;
        const matched = procs.find((p) => pdfUpper.includes(p.procIdent)) ??
          procs.find((p) => procIdent.includes(p.procIdent)) ??
          null;

        const seen = new Set<string>();
        const cifpFixes: EditorFix[] = [];
        // FAC magnetic course extracted from the procedure's FAF→MAP
        // CF leg — hoisted here so it's in scope for the NASR-magvar
        // conversion done after the fetchNasrAirport() call below.
        let facMag: number | null = null;
        if (matched) {
          for (const trans of matched.transitions ?? []) {
            for (const leg of trans.legs ?? []) {
              if (!leg.fixIdent || leg.fixLat == null || leg.fixLon == null) continue;
              if (seen.has(leg.fixIdent)) continue;
              seen.add(leg.fixIdent);
              const ltype = leg.legType ?? '';
              let type = 'custom';
              if (leg.isMissedApproach) type = 'missed';
              else if (ltype === 'CF') type = 'tick';
              else if (leg.fixIdent.startsWith('RW')) type = 'map';
              else type = 'iaf';
              cifpFixes.push({
                ident: leg.fixIdent,
                lat: leg.fixLat,
                lon: leg.fixLon,
                type,
                source: 'cifp',
              });
            }
          }
          // Find the FAC: prefer the magnetic course on the FAF→MAP
          // CF leg (final approach segment). DON'T use holdInboundCourse
          // — that's the missed-approach holding inbound, often the
          // reciprocal of the FAC and very misleading.
          //
          // Boss report 2026-05-19 23:49 PT: KRBL VOR RWY 15 has
          // published FAC 149°M, but the corrector showed 341°T
          // (which is the missed-approach hold inbound on RBL R-329,
          // 329+17°E magvar ≈ 346°T — close to the reported 341°).
          //
          // Algorithm: walk all approach (non-missed) legs, pick the
          // last CF leg's magCourse — that's the FAF→MAP segment which
          // is the published FAC. Then convert mag→true via the
          // airport's magVar.
          for (const trans of matched.transitions ?? []) {
            for (const leg of trans.legs ?? []) {
              const l = leg as unknown as { magCourse?: number | null; legType?: string; isMissedApproach?: boolean };
              if (l.isMissedApproach) continue;
              if (l.legType === 'CF' && l.magCourse != null) {
                facMag = l.magCourse;
              }
            }
          }
          if (facMag == null) {
            // No CF leg — fall back to any non-missed leg with magCourse
            for (const trans of matched.transitions ?? []) {
              for (const leg of trans.legs ?? []) {
                const l = leg as unknown as { magCourse?: number | null; isMissedApproach?: boolean };
                if (l.isMissedApproach) continue;
                if (l.magCourse != null) { facMag = l.magCourse; break; }
              }
              if (facMag != null) break;
            }
          }
          // facMag captured here; magvar conversion happens after
          // the fetchNasrAirport() call below.
        }

        // Append NASR-suggested waypoints. Even when CIFP has fixes, the
        // suggester adds plate-text/geographic candidates that the legacy
        // CIFP leg list misses (e.g. missed-approach holding fixes named
        // on the plate but not in the IAP record).
        let suggested: EditorFix[] = [];
        if (data?.cycle) {
          const resp = await fetchSuggestedWaypoints(plate.pdfName, data.cycle, ident);
          if (resp) {
            suggested = resp.candidates
              .filter((c) => !seen.has(c.ident))
              .map(suggestedToEditorFix);
            setEditorSuggesterMode(resp.mode);
          }
        }

        // Prepend the airport ARP itself as a pinnable waypoint. The
        // depicted runway/airport symbol on the plate is a known GPS
        // point — pinning it gives one of the two anchors needed to
        // geolocate. (Boss request 2026-05-18 evening, re-reported
        // 2026-05-19 21:54 PT when the feature turned out to only be
        // wired into the inline fallback path in PlateCorrectionsAdminPage,
        // NOT into ProcedureTab which is what runs for deep-linked
        // plates via `?openPlate=...&autoEditor=1`. This duplicates
        // the ARP-injection logic from PlateCorrectionsAdminPage so
        // both paths get the airport-pin option.)
        let airportFix: EditorFix | null = null;
        try {
          const nasr = await fetchNasrAirport(ident);
          if (nasr && Number.isFinite(nasr.airport.lat) && Number.isFinite(nasr.airport.lon)) {
            airportFix = {
              // 'ARP' = Airport Reference Point (standard aviation
              // acronym). Verified 2026-05-19 22:02 PT against the
              // live NASR lookup that no real NAVAID or fix uses
              // this ident, so it can't collide with a checklist
              // entry. We use a label distinct from the airport's
              // own ident (e.g. '12N') so the cartographer doesn't
              // confuse the ARP pin with a fix at the same location.
              ident: 'ARP',
              lat: nasr.airport.lat,
              lon: nasr.airport.lon,
              type: 'airport',
              source: 'cifp',
              name: `${nasr.airport.name} — Airport Reference Point`,
            };
          }
          // Apply magvar conversion to facMag now that we have NASR data.
          // NASR magVar is east-positive; true = mag + magvar.
          // (Falls back to facMag uncorrected if magvar isn't available
          // — better than nothing, and the warning's ±10° tolerance
          // accommodates the slop.)
          if (facMag != null) {
            const mv = nasr?.airport?.magVar;
            const facTrue = mv != null ? ((facMag + mv) % 360 + 360) % 360 : facMag;
            setEditorCourse(facTrue);
          }
        } catch {
          // Non-fatal — if NASR doesn't know this airport (foreign
          // dataset, unbuilt data, etc.), the cartographer can still
          // pin navaids/fixes; the airport just won't be offered
          // as a pre-built option.
        }
        // Belt-and-suspenders: drop any CIFP / suggested fix that
        // shares the airport's ident (CIFP legs reference fixes not
        // ARPs, but the suggester occasionally emits the airport).
        const cifpFiltered = airportFix
          ? cifpFixes.filter((f) => f.ident !== airportFix?.ident)
          : cifpFixes;
        const suggestedFiltered = airportFix
          ? suggested.filter((f) => f.ident !== airportFix?.ident)
          : suggested;
        setEditorFixes([
          ...(airportFix ? [airportFix] : []),
          ...cifpFiltered,
          ...suggestedFiltered,
        ]);
      })
      .catch(() => { /* non-fatal: editor opens without pre-loaded fixes */ });
  // openPdfName added to deps so the effect re-runs when a new deep-link
  // arrives while ProcedureTab is already mounted (already-mounted race fix).
  // autoEditor intentionally omitted — it's stable per deep-link invocation
  // and the guard (deepLinkApplied.current) prevents duplicate launches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, data, openPdfName]);

  const cycle = data?.cycle ?? '';

  const handleViewOnMap = (p: PlateRecord) => {
    // Push the plate spec without corners. Client looks up server-side
    // georef via /api/plates/georef. Plates with status='ok' in the
    // index get vision-based affine corners. Others fall back to a
    // CIFP bbox (rough) until vision georef is run for them.
    setPlateOverlay({
      cycle,
      pdfName: p.pdfName,
      chartName: p.chartName,
      airportIdent: ident,
    });
    nav('/map');
  };

  return (
    <div className="relative">
      <nav role="tablist" className="flex border-b border-cockpit-700 sticky top-0 bg-cockpit-800/95 backdrop-blur z-10">
        {SUBTABS.map((s) => {
          const count =
            !groups ? 0 :
            s.key === 'airport' ? groups.diagram.length + groups.minimums.length + groups.other.length :
            s.key === 'departure' ? groups.departure.length :
            s.key === 'arrival' ? groups.arrival.length :
            groups.approach.length;
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={sub === s.key}
              onClick={() => setSub(s.key)}
              className={[
                'flex-1 text-[11px] py-2 px-2 transition-colors whitespace-nowrap',
                sub === s.key
                  ? 'text-ember-400 border-b-2 border-ember-500'
                  : 'text-cockpit-300 hover:text-cockpit-100 border-b-2 border-transparent',
              ].join(' ')}
            >
              {s.label}
              {count > 0 && (
                <span className="ml-1 text-[10px] text-cockpit-500 font-mono">({count})</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-3 py-3 text-sm">
        {loading && <div className="text-cockpit-300 text-xs">Loading plates…</div>}
        {error && <div className="text-red-400 text-xs">⚠ {error}</div>}
        {!loading && !error && data && groups && (
          <>
            {sub === 'airport' && (
              <PlateBucket
                plates={[...groups.diagram, ...groups.minimums, ...groups.other]}
                emptyLabel="No airport diagram or terminal-area products published."
                onPick={(p) => setSelected(p)}
                georefMap={georefMap}
              />
            )}
            {sub === 'departure' && (
              <PlateBucket
                plates={groups.departure}
                emptyLabel="No published departure procedures."
                onPick={(p) => setSelected(p)}
                georefMap={georefMap}
              />
            )}
            {sub === 'arrival' && (
              <PlateBucket
                plates={groups.arrival}
                emptyLabel="No published STAR arrivals."
                onPick={(p) => setSelected(p)}
                onViewOnMap={handleViewOnMap}
                georefMap={georefMap}
              />
            )}
            {sub === 'approach' && (
              <PlateBucket
                plates={groups.approach}
                emptyLabel="No published approach procedures."
                onPick={(p) => setSelected(p)}
                onViewOnMap={handleViewOnMap}
                georefMap={georefMap}
                selectedPdfName={selected?.pdfName}
                airportIcao={ident}
                airportElevFt={airportElevFt}
              />
            )}
          </>
        )}
        {data && <PlatesCycleFooter cycle={data.cycle} effective={data.effective} expires={data.expires} />}
      </div>

      {selected && cycle && (
        <PlateViewer
          cycle={cycle}
          pdfName={selected.pdfName}
          title={selected.chartName}
          subtitle={`${ident} · ${chartCodeLabel(selected.chartCode)}${selected.amdtNum ? ` · Amdt ${selected.amdtNum}` : ''}`}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Correction editor: auto-launched via deep-link or opened
          manually. Mounted on top of the PlateViewer (same z-level
          since editor is full-screen). */}
      {editorPlate && cycle && (
        <PlateCorrectionEditor
          cycle={cycle}
          pdfName={editorPlate.pdfName}
          airport={ident}
          procedureIdent={editorPlate.chartName}
          fixes={editorFixes}
          suggesterMode={editorSuggesterMode}
          courseDegTrue={editorCourse}
          onClose={() => setEditorPlate(null)}
          onSaved={() => {
            setEditorPlate(null);
            // Re-fetch georef badges so the corrected plate updates its badge.
            if (data?.cycle) {
              fetchAirportGeorefStatus(ident, data.cycle)
                .then(setGeorefMap)
                .catch(() => undefined);
            }
          }}
        />
      )}
    </div>
  );
}

// (GeorefBadge moved to src/components/GeorefBadge.tsx on 2026-05-16
// Track 2 so PlanPanel, PlatesPage, and the Procedure Brief panel can
// reuse the same legend without duplicating the cfg table.)

function PlateBucket({
  plates, emptyLabel, onPick, onViewOnMap, georefMap, selectedPdfName, airportIcao, airportElevFt,
}: {
  plates: PlateRecord[];
  emptyLabel: string;
  onPick: (p: PlateRecord) => void;
  onViewOnMap?: (p: PlateRecord) => void;
  georefMap: Map<string, PlateGeorefIndexRow>;
  /** PDF name of the currently-selected plate (for inline chip rendering). */
  selectedPdfName?: string;
  /** Airport ICAO code for the circling-minima chip (approach bucket only). */
  airportIcao?: string;
  /** Airport field elevation (MSL ft) for the circling-minima chip. */
  airportElevFt?: number;
}) {
  if (plates.length === 0) {
    return <div className="text-cockpit-400 text-xs italic">{emptyLabel}</div>;
  }
  return (
    <ul className="space-y-1">
      {plates.map((p) => {
        const row = georefMap.get(p.pdfName.toUpperCase());
        const quality = plateGeorefQuality(row);
        const isSelected = selectedPdfName === p.pdfName;
        // Derive proc ident for circling-minima chip (IAP plates only)
        const procIdent = airportIcao ? deriveProcIdent(p.chartName) : null;
        return (
          <li key={p.pdfName}>
            <div className="flex items-stretch gap-1">
              <button
                onClick={() => onPick(p)}
                className={[
                  'flex-1 text-left rounded-md px-3 py-2 border transition-colors min-w-0',
                  isSelected
                    ? 'bg-cockpit-700/60 border-cockpit-500 text-cockpit-100'
                    : 'bg-cockpit-700/30 border-cockpit-700 hover:bg-cockpit-700/60 text-cockpit-100',
                ].join(' ')}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon name="plate" className="h-4 w-4 text-cockpit-300 shrink-0" />
                  <span className="text-sm truncate flex-1">{p.chartName}</span>
                  <GeorefBadge quality={quality} />
                  <span className="text-[10px] font-mono text-cockpit-400 shrink-0">
                    {chartCodeLabel(p.chartCode)}
                  </span>
                </div>
                {(p.amdtNum || p.amdtDate) && (
                  <div className="mt-0.5 text-[10px] text-cockpit-400 font-mono">
                    {p.pdfName}{p.amdtNum ? ` · Amdt ${p.amdtNum}` : ''}{p.amdtDate ? ` · ${p.amdtDate}` : ''}
                  </div>
                )}
                {/* Circling minima chip — rendered inline when this approach
                    plate is selected and we have a derivable proc ident */}
                {isSelected && airportIcao && procIdent && (
                  <CirclingMinimaChip
                    airportIcao={airportIcao}
                    procIdent={procIdent}
                    airportElevFt={airportElevFt ?? 0}
                  />
                )}
              </button>
              {onViewOnMap && (
                <button
                  onClick={() => onViewOnMap(p)}
                  title={
                    quality === 'failed'
                      ? 'View on Map (no automated georef — will use CIFP bbox)'
                      : 'View on Map'
                  }
                  className="shrink-0 rounded-md px-2 bg-cockpit-700/30 border border-cockpit-700 hover:bg-ember-600/40 hover:border-ember-500 text-cockpit-300 hover:text-ember-300 transition-colors text-sm"
                >
                  🗺
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PlatesCycleFooter({ cycle, effective, expires }: { cycle: string; effective: string; expires: string }) {
  const snapshot = usePublicationsStore((s) => s.snapshot);
  const ensureFresh = usePublicationsStore((s) => s.ensureFresh);
  useEffect(() => { void ensureFresh(); }, [ensureFresh]);

  const dtpp = snapshot?.publications.find((p) => p.id === 'dtpp');
  const status = dtpp?.status ?? 'unknown';

  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] font-mono text-cockpit-400">
      <span>d-TPP cycle {cycle} · eff {effective} → {expires}</span>
      {dtpp && status !== 'unknown' && (
        <span className={statusTextClass(status)}>
          ({statusLabel(status)}{dtpp.daysUntilExpiry != null && status !== 'current' ? `, ${dtpp.daysUntilExpiry < 0 ? `${Math.abs(dtpp.daysUntilExpiry)}d ago` : `${dtpp.daysUntilExpiry}d left`}` : ''})
        </span>
      )}
    </div>
  );
}
